import type { FC } from 'react';
import React, { useState } from 'react';
import cx from 'classnames';
import { MDText } from 'i18n-react';

import { Modal } from '@motorway/motorway-storybook-cra';
import { Button, FaceHappyIcon, FaceSadIcon } from '@motorway/mw-highway-code';
import { toast } from '@motorway/mw-highway-code/pre-alpha';

import cypressIds from 'Components/cypress_ids.json';
import { NumericGradingText } from 'Components/NumericGradingText/NumericGradingText';
import ExpandableListItem from 'Components/shared/ExpandableListItem/ExpandableListItem';
import componentTexts from 'Components/texts.json';
import { useVehicleData } from 'Context/VehicleContext/VehicleDataContext';
import type { NumericGradeValues } from 'Pages/vehicles/helpers/vehicle.schema';
import {
	triggerGradingModalAccordionItemClosedByOtherEvent,
	triggerGradingModalAccordionOpenedOrClosedEvent,
	triggerGradingModalNoButtonClickedEvent,
	triggerGradingModalYesButtonClickedEvent,
} from 'Services/analytics/events';
import { NumericGrade } from 'Types/grade';
import { applyCypressData } from 'Utilities/helpers';

import texts from './ExteriorNumericGradingModal.texts';
import { NegativeFeedbackForm } from './NegativeFeedbackForm';

import styles from './ExteriorNumericGradingModal.module.scss';

const LocalT = new MDText(texts);
const ComponentT = new MDText(componentTexts);

const NUMERIC_GRADE_DESCENDING_ORDER = [
	NumericGrade[1],
	NumericGrade[2],
	NumericGrade[3],
	NumericGrade[4],
	NumericGrade[5],
];

const ModalContent: FC<{ onDismissModal?: () => void }> = ({ onDismissModal }) => {
	const { ratings } = useVehicleData();
	const [selectedNumericGrade, setSelectedNumericGrade] = useState<Nullable<NumericGradeValues | ''>>(
		ratings.numericGrade,
	);
	const [shouldShowNegativeFeedbackForm, setShouldShowNegativeFeedbackForm] = useState(false);

	const onClick = (numericGrade: NumericGradeValues) => {
		const shouldClose = numericGrade === selectedNumericGrade;
		const isClosingOtherGradeAccordion = !!(selectedNumericGrade && !shouldClose);

		setSelectedNumericGrade(shouldClose ? '' : numericGrade);

		const gradeTranslation = ComponentT.translate(`vehicleList.filters.numericGrades.${numericGrade}`);

		if (shouldClose) {
			triggerGradingModalAccordionOpenedOrClosedEvent({
				grade: `${gradeTranslation}`,
				isExpand: false,
			});
			return;
		}

		if (isClosingOtherGradeAccordion) {
			triggerGradingModalAccordionItemClosedByOtherEvent(
				`${ComponentT.translate(`vehicleList.filters.numericGrades.${selectedNumericGrade}`)}`,
			);
		}

		triggerGradingModalAccordionOpenedOrClosedEvent({
			grade: `${gradeTranslation}`,
			isExpand: true,
		});
	};

	return (
		<div className={styles.modalContentContainer} {...applyCypressData(cypressIds.containers.gradingModal)}>
			<div className={styles.headerContainer}>
				<h3>{LocalT.translate(texts.header.heading)}</h3>
				<p className={styles.description}>{LocalT.translate(texts.header.description)}</p>
			</div>
			<ul className={styles.accordionContainer}>
				{NUMERIC_GRADE_DESCENDING_ORDER.map((grade) => {
					const translation = LocalT.translate(`accordions.grade${grade}.description`);
					return (
						<ExpandableListItem
							key={`grade-${grade}-accordion-item`}
							classNames={{
								container: styles.itemContainer,
								content: styles.accordionItemContent,
								header: styles.accordionItemHeader,
							}}
							id={`grade-${grade}-accordion-item`}
							isExpanded={selectedNumericGrade === grade}
							onClick={() => onClick(grade)}
							title={<NumericGradingText grade={grade} size="large" />}
						>
							{`${translation}`}
						</ExpandableListItem>
					);
				})}
			</ul>
			<div className={styles.footer}>
				<div className={styles.footerTextContainer}>
					<h4>{LocalT.translate(texts.footer.question)}</h4>
					<p className={styles.footerDescription}>{LocalT.translate(texts.footer.description)}</p>
				</div>
				<div className={cx(styles.footerCTAContainer, shouldShowNegativeFeedbackForm && styles.active)}>
					<Button
						reverse
						icon={FaceHappyIcon}
						label={LocalT.translate(texts.footer.buttons.yes)}
						onClick={() => {
							toast({
								content: LocalT.translate('toast.content'),
								heading: LocalT.translate('toast.heading'),
								variant: 'success',
							});
							triggerGradingModalYesButtonClickedEvent();
							onDismissModal?.();
						}}
						variant="secondary"
					/>
					<Button
						reverse
						icon={FaceSadIcon}
						label={LocalT.translate(texts.footer.buttons.no)}
						onClick={() => {
							triggerGradingModalNoButtonClickedEvent();
							setShouldShowNegativeFeedbackForm(true);
						}}
						variant="secondary"
					/>
				</div>
				{shouldShowNegativeFeedbackForm && <NegativeFeedbackForm onDissmissModal={onDismissModal} />}
			</div>
		</div>
	);
};

interface ExteriorNumericGradingModalProps {
	onDismissModal?: () => void;
}

export const ExteriorNumericGradingModal: FC<ExteriorNumericGradingModalProps> = ({ onDismissModal }) => {
	return (
		<Modal
			contentClass={styles.modalContent}
			dialogClass={styles.modalDialog}
			dismissModal={onDismissModal}
			size="small"
		>
			<ModalContent onDismissModal={onDismissModal} />
		</Modal>
	);
};
